import { usePatient } from "@repo/data-store";
import { Tabs, TabsList, TabsTrigger } from "@repo/ui/components/ui/tabs";
import { createFileRoute, Outlet, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { PatientHeader } from "@/features/patients";
import { patientDisplayName } from "@/shared/lib/patient-utils";

export const Route = createFileRoute("/patients/$patientId/_layout")({
  component: RouteComponent,
});

const TABS = [
  { value: "overview", label: "Overview", to: "/patients/$patientId" },
  { value: "cases", label: "Cases", to: "/patients/$patientId/cases" },
  { value: "care-plans", label: "Care Plans", to: "/patients/$patientId/care-plans" },
  { value: "notes", label: "Notes", to: "/patients/$patientId/notes" },
  { value: "assessments", label: "Assessments", to: "/patients/$patientId/assessments" },
  { value: "visits", label: "Visits", to: "/patients/$patientId/visits" },
  { value: "documents", label: "Documents", to: "/patients/$patientId/documents" },
  { value: "reports", label: "Reports", to: "/patients/$patientId/reports" },
  { value: "profile", label: "Profile", to: "/patients/$patientId/profile" },
] as const;

function RouteComponent() {
  const { patientId } = Route.useParams();
  const { patient, isLoading } = usePatient(patientId);
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  const activeTab =
    TABS.slice(1).find((t) => matchRoute({ to: t.to, params: { patientId }, fuzzy: true }))?.value ?? "overview";

  // Workbench renders under the Overview tab but wants a full-height, full-bleed body.
  const isWorkbench = !!matchRoute({ to: "/patients/$patientId/workbench", params: { patientId } });

  // Assessments scrolls its rail and detail pane independently, and Notes scrolls
  // its editor in place, so the page itself must not grow.
  const isTwoPane = activeTab === "assessments";
  const isFullHeight = isTwoPane || activeTab === "notes";

  if (isLoading) {
    return <div className="p-4">Loading patient...</div>;
  }

  if (!patient) {
    return <div className="p-4">Patient not found</div>;
  }

  const patientName = patientDisplayName(patient) ?? "";

  return (
    <div className={`flex flex-col pt-4 ${isFullHeight ? "h-full min-h-0" : "min-h-full"}`}>
      <PatientHeader patient={patient} patientName={patientName} />
      <Tabs data-patient-navigation value={activeTab} className="sticky top-0 -mt-4 pt-4 border-b bg-background z-10">
        <TabsList variant={"line"}>
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} onClick={() => navigate({ to: t.to, params: { patientId } })}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div
        className={
          isWorkbench
            ? "flex flex-1 flex-col"
            : isTwoPane
              ? "flex min-h-0 flex-1 flex-col"
              : activeTab === "notes"
                ? "flex min-h-0 flex-1 flex-col"
                : activeTab === "visits"
                  ? "flex flex-1 flex-col"
                  : activeTab === "cases" || activeTab === "care-plans" || activeTab === "documents"
                    ? "flex flex-1 flex-col p-4"
                    : "p-4"
        }
      >
        <Outlet />
      </div>
    </div>
  );
}
